/**
 * Active-tenant badge + switcher (Phase 8).
 *
 * Shown only when the user can act in more than one tenant (multi-membership)
 * or is ROOT (platform scope). Selecting a tenant calls the backend switch
 * endpoint (which mints a new JWT with the `act` claim), reloads the effective
 * permission set, and folds the result back into `loginUser` via `userdata` so
 * route guards and menus recompute. The server re-authorizes the switch — a
 * user who isn't a member gets a 403 and we surface it.
 */

import React from 'react';
import { Box, Chip, Menu, MenuItem, ListItemText, Tooltip, CircularProgress } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import swal from 'sweetalert';
import { applyTenantSwitch } from '../utilityFunctions/TenantUtil';
import { isRoot } from './AccessControl';

export default function TenantSwitcher({ loginUser, userdata }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const memberships = (loginUser && Array.isArray(loginUser.memberships)) ? loginUser.memberships : [];
  const activeId = loginUser && loginUser.activeTenantId != null ? String(loginUser.activeTenantId) : null;
  const root = isRoot(loginUser);

  // Only worth showing when there's a real choice to make.
  if (!root && memberships.length <= 1) return null;

  const activeMembership = memberships.find((m) => String(m.tenantId) === activeId);
  const activeLabel = (activeMembership && (activeMembership.displayName || activeMembership.slug))
    || (loginUser && loginUser.activeTenantSlug)
    || (activeId ? `Tenant ${activeId}` : 'No tenant');

  const handleSelect = async (tenantId) => {
    setAnchorEl(null);
    if (String(tenantId) === activeId) return;
    setBusy(true);
    const merged = await applyTenantSwitch(tenantId, loginUser && loginUser.jwtToken, memberships);
    setBusy(false);
    if (!merged) {
      swal({ title: 'Switch failed', text: 'You may not have access to that tenant.', icon: 'error' });
      return;
    }
    userdata(merged);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={root ? 'Active tenant (ROOT can switch)' : 'Active tenant'}>
        <Chip
          icon={busy ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <ApartmentIcon />}
          deleteIcon={<ExpandMoreIcon />}
          onDelete={memberships.length > 0 ? (e) => setAnchorEl(e.currentTarget) : undefined}
          onClick={memberships.length > 0 ? (e) => setAnchorEl(e.currentTarget) : undefined}
          label={activeLabel}
          disabled={busy}
          sx={{
            backgroundColor: 'white',
            color: 'black',
            fontWeight: 600,
            maxWidth: { xs: 120, sm: 220 },
            '& .MuiChip-icon': { color: 'black' },
            '& .MuiChip-deleteIcon': { color: 'black' },
          }}
        />
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {memberships.map((m) => (
          <MenuItem
            key={m.tenantId}
            selected={String(m.tenantId) === activeId}
            onClick={() => handleSelect(m.tenantId)}
          >
            <ListItemText
              primary={m.displayName || m.slug}
              secondary={`${m.slug}${m.status && m.status !== 'ACTIVE' ? ` · ${m.status}` : ''}`}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
